import React, { useState, useEffect, useContext } from 'react';
import SocketContext from '../../../../../../../NewWebSocket/components/WebSocketAdapter';
import './GameTimeControl.css';

interface GameTimeControlProps {
  championId: string;
  currentGameTime: number; // Game time in minutes
  onGameTimeUpdate?: (gameTime: number) => void;
}

const GameTimeControl: React.FC<GameTimeControlProps> = ({
  championId,
  currentGameTime,
  onGameTimeUpdate
}) => {
  const socket = useContext(SocketContext);
  const [gameTime, setGameTime] = useState(currentGameTime);
  const MIN_TIME = 0;
  const MAX_TIME = 60; // Max 60 minutes
  
  // Update local state when prop changes
  useEffect(() => {
    setGameTime(currentGameTime);
  }, [currentGameTime]);
  
  // Handler for directly setting game time via input
  const handleGameTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseFloat(e.target.value);
    if (!isNaN(newTime)) {
      const clampedTime = Math.min(Math.max(newTime, MIN_TIME), MAX_TIME);
      setGameTime(clampedTime);
    }
  };
  
  // Handler for applying game time change
  const applyGameTimeChange = () => {
    console.log(`[GameTimeControl] Setting champion ${championId} game time to ${gameTime} minutes`);
    
    // Use onGameTimeUpdate if available (static mode), otherwise use socket (WebSocket mode)
    if (onGameTimeUpdate) {
      onGameTimeUpdate(gameTime);
    } else if (socket) {
      socket.emit('set-game-time', {
        championId: championId,
        gameTime: gameTime
      });
    }
  };

  // Spinner button handlers
  const incrementTime = () => {
    const newTime = Math.min(gameTime + 1, MAX_TIME);
    setGameTime(newTime);
  };

  const decrementTime = () => {
    const newTime = Math.max(gameTime - 1, MIN_TIME);
    setGameTime(newTime);
  };

  // Format display: show "MM:00" for whole minutes
  const formatTime = (minutes: number): string => {
    const mins = Math.floor(minutes);
    const secs = Math.round((minutes - mins) * 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="game-time-control">
      <div className="game-time-control-container">
        <div className="game-time-input-row">
          <h4>Game Time</h4>
          <div className="game-time-input-container">
            <input
              type="number"
              min={MIN_TIME}
              max={MAX_TIME}
              step={1}
              value={gameTime}
              onChange={handleGameTimeChange}
              className="game-time-input"
            />
            <div className="custom-spinner">
              <button className="spinner-button" onClick={incrementTime}></button>
              <button className="spinner-button" onClick={decrementTime}></button>
            </div>
          </div>
          
          <span className="game-time-unit">min</span>
          
          <button 
            onClick={applyGameTimeChange}
            className="apply-button"
          >
            Apply
          </button>
        </div>
        <span className="game-time-display">{formatTime(gameTime)}</span>
      </div>
    </div>
  );
};

export default GameTimeControl;
